import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { TablePage, TableRequest } from './softsee'
import type { Period } from './securitee'

/** Printeeng - /api/v2/printeeng/*; perechile vechi sunt in `PrinteengController`. */

function params(r: Omit<TableRequest, 'list'>, extra: Record<string, string> = {}): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  for (const [k, v] of Object.entries(extra)) p.set(k, v)
  return p
}

export type Row = Record<string, unknown>

export function devicesTableQuery(r: Omit<TableRequest, 'list'> & Period) {
  return queryOptions({ queryKey: ['printeeng', 'devices', r], queryFn: () => apiGet<TablePage<Row>>(`/printeeng/devices-table?${params(r, { from: r.from, to: r.to })}`) })
}

export function printersTableQuery(r: Omit<TableRequest, 'list'> & Period) {
  return queryOptions({ queryKey: ['printeeng', 'printers', r], queryFn: () => apiGet<TablePage<Row>>(`/printeeng/printers-table?${params(r, { from: r.from, to: r.to })}`) })
}

export function eventsTableQuery(r: Omit<TableRequest, 'list'> & Period) {
  return queryOptions({ queryKey: ['printeeng', 'events', r], queryFn: () => apiGet<TablePage<Row>>(`/printeeng/events-table?${params(r, { from: r.from, to: r.to })}`) })
}

export interface PrinteengOverview {
  currency: string
  counters: { monitored_devices: number; excedeed_quota: number; total_pages: number; total_costs: number }
}

export function overviewQuery() {
  return queryOptions({ queryKey: ['printeeng', 'overview'], queryFn: () => apiGet<PrinteengOverview>('/printeeng/overview') })
}

export interface PrinteengSummary {
  currency: string
  pages: number
  costs?: number
  co2: string
  cut_trees: string
  quota?: { profile: string; printed: string; remained: string; extra: string }
  loggedUserSummary: { logged_user: string; pages: number; co2: string; cut_trees: string }[]
}

export function summaryQuery(kind: 'devices' | 'printers', id: number) {
  return queryOptions({ queryKey: ['printeeng', kind, id, 'summary'], queryFn: () => apiGet<PrinteengSummary>(`/printeeng/${kind}/${id}/summary`) })
}

export function nestedTableQuery(kind: 'devices' | 'printers', id: number, what: 'files' | 'events', r: Omit<TableRequest, 'list'> & Partial<Period>) {
  return queryOptions({
    queryKey: ['printeeng', kind, id, what, r],
    queryFn: () => apiGet<TablePage<Row>>(`/printeeng/${kind}/${id}/${what}/table?${params(r, r.from && r.to ? { from: r.from, to: r.to } : {})}`),
  })
}

export interface DeviceSettings {
  device: { id: number }
  profile: number
  block: number
  agent_message: number
  extra_quota: number | null
  profiles: { ID: number; name: string; quota: number; period: string }[]
}

export function deviceSettingsQuery(id: number) {
  return queryOptions({ queryKey: ['printeeng', 'devices', id, 'settings'], queryFn: () => apiGet<DeviceSettings>(`/printeeng/devices/${id}/settings`) })
}

export interface PrinterSettings {
  printer: { id: number; name: string }
  currency: string
  pricing_type: number
  price_per_page: number
  toner_type: string
  toner_price: number
  toner_pages: number
}

export function printerSettingsQuery(id: number) {
  return queryOptions({ queryKey: ['printeeng', 'printers', id, 'settings'], queryFn: () => apiGet<PrinterSettings>(`/printeeng/printers/${id}/settings`) })
}

export function post(path: string, fields: Record<string, PostValue> = {}) {
  return apiPost<{ status: string; message: string }>(path, fields)
}
